/**
 * @nanokart/tryon-studio-sdk
 * SDK API Service
 * 
 * Handles SDK-specific API calls authenticated via API key.
 * This provides loose coupling - SDK only needs the partner's API key
 * to communicate with api.nanokart.ai, no session required.
 */

import { CustomerContext } from '../../types/config';

export interface SDKAddToCartRequest {
  // Product info
  product_id: string;
  product_name: string;
  sku_id?: string;
  size?: string;
  color?: string;
  quantity?: number;
  price?: number;
  currency?: string;
  
  // Try-on images
  tryon_image_url?: string;
  garment_image_url?: string;
  
  // Customer context
  customer_phone?: string;
  customer_email?: string;
  customer_name?: string;
  customer_id?: string;
  
  // Additional metadata
  metadata?: Record<string, unknown>;
}

export interface SDKAddToCartResponse {
  success: boolean;
  webhook_invoked: boolean;
  reason?: string;
  status_code?: number;
  error?: string;
  message?: string;
}

export interface SDKPurchaseIntentRequest {
  product_id: string;
  product_name?: string;
  sku_id?: string;
  price?: number;
  quantity?: number;
  currency?: string;
  source?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_name?: string;
  customer_id?: string;
  metadata?: Record<string, unknown>;
}

export interface SDKPurchaseIntentResponse {
  success: boolean;
  intent_id?: string;
  message?: string;
}

export interface SDKConfigResponse {
  partner_id: string;
  features: {
    add_to_cart_webhook: boolean;
    moments: boolean;
    history: boolean;
  };
  branding: {
    company_name: string;
    logo_url: string;
  };
}

/**
 * SDK API Service
 * 
 * Provides API-key-authenticated endpoints for SDK functionality.
 * This keeps the SDK loosely coupled from Nanokart's session-based flows.
 */
export class SDKApiService {
  private apiBaseUrl: string;
  private apiKey: string;

  constructor(apiBaseUrl: string, apiKey: string) {
    this.apiBaseUrl = apiBaseUrl;
    this.apiKey = apiKey;
  }

  /**
   * Get SDK configuration for the partner.
   * Called during SDK initialization to check feature flags.
   */
  async getConfig(): Promise<SDKConfigResponse> {
    const response = await fetch(`${this.apiBaseUrl}/api/v1/sdk/config`, {
      method: 'GET',
      headers: {
        'X-Api-Key': this.apiKey,
      },
    });

    if (!response.ok) {
      console.error('[SDKApi] Failed to get config:', response.status);
      // Return default config on error
      return {
        partner_id: '',
        features: {
          add_to_cart_webhook: false,
          moments: true,
          history: true,
        },
        branding: {
          company_name: '',
          logo_url: '',
        },
      };
    }

    return response.json();
  }

  /**
   * Invoke Add to Cart webhook.
   * Called when customer clicks "Add to Cart" in the SDK.
   * 
   * @param productData - Product and try-on information
   * @param customerContext - Customer information provided by partner
   */
  async addToCart(
    productData: Omit<SDKAddToCartRequest, 'customer_phone' | 'customer_email' | 'customer_name' | 'customer_id'>,
    customerContext?: CustomerContext
  ): Promise<SDKAddToCartResponse> {
    const payload: SDKAddToCartRequest = {
      ...productData,
      // Merge customer context
      customer_phone: customerContext?.phone,
      customer_email: customerContext?.email,
      customer_name: customerContext?.name,
      customer_id: customerContext?.customerId,
    };

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/v1/sdk/add-to-cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.apiKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[SDKApi] Add to cart failed:', error);
      return {
        success: false,
        webhook_invoked: false,
        error: 'network_error',
        message: 'Failed to connect to Nanokart API',
      };
    }
  }

  /**
   * Record a purchase intent event.
   * Called whenever the customer clicks "Add to Cart" in the SDK.
   * Feeds the ROI calculation in the Partners Dashboard.
   */
  async recordPurchaseIntent(
    data: SDKPurchaseIntentRequest
  ): Promise<SDKPurchaseIntentResponse> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/v1/purchase-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.apiKey,
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      // Silent fail — purchase intent recording should never block cart UX
      console.error('[SDKApi] recordPurchaseIntent failed:', error);
      return { success: false };
    }
  }
}
